import { Alert, Grid, Group, Paper, Space, Text, Title } from '@mantine/core';
import { joiResolver, useForm } from '@mantine/form';
import { UseFormReturnType } from '@mantine/form/lib/use-form';
import { useEffect, useRef, useState } from 'react';
import { AlertCircle } from 'tabler-icons-react';
import { BusinessTypes } from '../../../enums/business-type';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';
import {
  GeneralInfoNonStarterSchema,
  GeneralInfoStarterSchema,
} from '../../../schemas/general-info.schema';
import { ErrorResponse } from '../../../type/error-response.types';
import { MerchantOnboardingFormValues } from '../../../type/merchant-onboarding-form-values.types';
import StepperButton from '../../buttons/stepper.button/stepper.button';
import MainOfficeContactInfo from './components/main-office-contact-info.component';
import MerchantDescription from './components/merchant-description.component';
import MerchantInfo from './components/merchant-info.component';
import WebsiteLinks from './components/website-links.component';
import './general-info-step.styles.css';

interface GeneralInformationStepProps {
  mainForm: UseFormReturnType<MerchantOnboardingFormValues>;
  active: number;
  activated: boolean;
  nextStep: () => void;
  prevStep: () => void;
}
function GeneralInformationStep(props: GeneralInformationStepProps) {
  const { mainForm, active, activated, nextStep, prevStep } = props;
  const [loading, setLoading] = useState(false);
  const [businessHandleError, setBusinessHandleError] = useState('');
  const axiosPrivate = useAxiosPrivate();
  const submitRef = useRef();
  const BUSINESS_HANDLE_VALIDATION_URL = '/web/onboard/businesshandles';

  const form = useForm({
    initialValues: mainForm.values,
    schema:
      mainForm.values.businessTypeId !== BusinessTypes.STARTER
        ? joiResolver(GeneralInfoNonStarterSchema)
        : joiResolver(GeneralInfoStarterSchema),
  });
  useEffect(() => {
    if (!activated) {
      return;
    }
    form.setValues(mainForm.values);
    return () => {};
  }, [active]);
  const handleNextStep = () => {
    setLoading(true);
    mainForm.setValues(form.values);
    setLoading(false);
    if (!loading) {
      nextStep();
    }
  };
  const handlePrevStep = () => {
    setLoading(true);
    mainForm.setValues(form.values);
    form.clearErrors();
    setLoading(false);
    if (!loading) prevStep();
  };

  const handleSubmit = async () => {
    if (form.values.businessTypeId === BusinessTypes.STARTER) {
      //Move to next because there is no need to validate business starter
      //business handle since it has none.
      // @ts-ignore
      submitRef?.current?.click();
      return;
    }

    setLoading(true);
    let hasBusinessErr = false;

    if (form.values.businessHandle?.trim().length > 0) {
      try {
        await axiosPrivate.post(BUSINESS_HANDLE_VALIDATION_URL, {
          businessHandle: form.values.businessHandle,
        });
      } catch (err: any) {
        const error: ErrorResponse = err.response?.data;

        hasBusinessErr = true;
        //We do this because validation for businesshandle in schema is any()
        if (!/^([A-Za-z]|[0-9]|_|-)+$/.test(form.values.businessHandle)) {
          form.setFieldError(
            'businessHandle',
            'Invalid format for payment link please contact support for assistance'
          );
          setBusinessHandleError(
            'Invalid format for payment link please contact support for assistance'
          );
        } else if (error.error) {
          form.setFieldError('businessHandle', error.error);
          setBusinessHandleError(error.error);
        } else {
          form.setFieldError('businessHandle', 'An Unexpected Error Occurred');
          setBusinessHandleError('An Unexpected Error Occurred');
        }
      }
    }
    setLoading(false);
    // @ts-ignore
    if (!hasBusinessErr) submitRef?.current?.click();
  };
  return (
    <>
      <Title order={1}>General Information</Title>
      <Text color='gray' mb={20}>
        This would help us give you features that are tailor fit to your
        business.
      </Text>
      <form onSubmit={form.onSubmit(handleNextStep)}>
        <Paper
          className='general-info-step'
          p={25}
          shadow='md'
          withBorder
          sx={{
            '@media (max-width: 500px)': {
              margin: 0,
            },
          }}
          data-cy='general-info-form'
        >
          <Grid align='flex-start'>
            <Grid.Col>
              <MerchantInfo
                form={form}
                businessHandleError={businessHandleError}
                setBusinessHandleError={setBusinessHandleError}
              />
              <MainOfficeContactInfo form={form} />
              <MerchantDescription form={form} />
              <WebsiteLinks form={form} />
            </Grid.Col>
          </Grid>
        </Paper>
        <button
          type='submit'
          // @ts-ignore
          ref={submitRef}
          style={{ display: 'none' }}
          data-cy='submit'
        ></button>

        <Group
          style={{ width: '100%' }}
          grow
          position='apart'
          align='flex-start'
          mt={30}
        >
          {Object.keys(form.errors).length > 0 ? (
            <Alert
              icon={<AlertCircle size={16} />}
              title='Invalid fields'
              color='red'
              variant='outline'
            >
              Please check fields for errors
            </Alert>
          ) : (
            <Space />
          )}

          <StepperButton
            prevStep={handlePrevStep}
            active={active}
            nextStep={undefined}
            loading={loading}
            onClick={handleSubmit}
          />
        </Group>
      </form>
    </>
  );
}

export default GeneralInformationStep;
